import React, { forwardRef, useContext } from 'react';
import { css, useTheme } from '@emotion/react';
import { type CSSInterpolation } from '@emotion/serialize';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { cs } from '@bbkAdminUtils/css-utils';
import { useMediaQueries } from '@bbkFunnel/components/modals/sections/media-queries';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';

import './button.scss';

const base = css({
  color: '#fff',
  padding: '8px 14px',
  fontFamily: 'inherit',
  fontWeight: 'normal',
  fontSize: '1rem',
  border: 'none',
  WebkitTouchCallout: 'none',
  userSelect: 'none',
  cursor: 'pointer',
  minHeight: '40px',
  height: 'auto',
  '&:focus': {
    outline: 'none',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.3,
  },
});

type Props = {
  classes?: string;
  specificStyles?: CSSInterpolation;
  transparent?: boolean;
  reverse?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const FunnelButton = forwardRef<HTMLButtonElement, Props>((pp, ref) => {
  const { classes, specificStyles, transparent, reverse, ...btnProps } = pp;
  const theme = useTheme() as EmotionTheme;
  const { mobileView } = useMediaQueries();
  const customCardTheme = theme.cardLevelTheme;
  const btnColor =
    (customCardTheme?.useCustom && customCardTheme?.button?.backgroundColor) ||
    theme.buttonBackgroundColor ||
    theme.primaryColor ||
    'inherit';
  const transparentStyles = {
    background: 'transparent',
    border: `2px solid ${btnColor}`,
    color: btnColor,
    padding: '8px 14px',
  };
  const equalButtonSize = theme.complianceEqualButtonSize;
  const consistentButtonStyle = theme.complianceConsistentButtonStyle;
  const offerContext = useContext(EditOfferPageContext);
  const isPreview = !!offerContext;
  const isMobilePreview =
    isPreview && offerContext?.previewDevice === PreviewDevice.MOBILE;

  const global = css([
    {
      color:
        (customCardTheme?.useCustom && customCardTheme?.button?.textColor) ||
        theme.buttonTextColor,
      background: btnColor,
      borderRadius: theme.buttonBorderRadius ?? theme.borderRadius,
      boxShadow: theme.boxShadow,
      textTransform: theme.buttonTextTransform,
      fontWeight: theme.buttonFontWeight,
      fontFamily: theme.buttonFontFamily,
      fontSize: theme.buttonFontSize,
      border: `2px solid ${btnColor}`,
    },
    transparent && transparentStyles,
  ]);
  const specificStylesCss = css(specificStyles ?? {});
  /*
    Page: width = 960, padding-x: 12px
    LA cards: gap 30px, margin-x: -10px
    Inline card:  1st padding-x: 2x10px, 2nd padding-x: 2x30px

    so button width will be 
    width = 960/2 - 12 - 30/2 + 10  - 20 - 60 = 383px
  */
  const equalButtonSizeCss = equalButtonSize
    ? css`
        font-size: ${theme.buttonFontSize ?? theme.fontSize} !important;
        font-weight: ${theme.buttonFontWeight} !important;
        line-height: 1.5 !important;
        width: ${mobileView || isMobilePreview ? '100%' : '383px'} !important;
        overflow: hidden !important;
      `
    : {};
  const consistentStyleCss = consistentButtonStyle
    ? css`
        color: ${
          (customCardTheme?.useCustom && customCardTheme?.button?.textColor) ||
          theme.buttonTextColor
        };
        background: ${btnColor};
        justify-content: center;
        display: flex;
        cursor: pointer !important;
        opacity: 1 !important;
        font-size: ${theme.buttonFontSize ?? theme.fontSize} !important;
        font-weight: ${theme.buttonFontWeight} !important;
        line-height: 1.5 !important;
      `
    : {};

  return (
    <button
      css={[
        base,
        global,
        specificStylesCss,
        reverse
          ? {
              background: theme.buttonTextColor,
              color: btnColor,
            }
          : {},
        equalButtonSizeCss,
        consistentStyleCss,
      ]}
      className={cs(classes, 'funnel-button', 'funnel-button-global')}
      ref={ref}
      {...btnProps}
    />
  );
});
